import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import ConfirmDialog from '../common/ConfirmDialog';
import { openFilePreview } from '../common/FilePreviewModal';
import { usePermissions } from '../../services/PermissionsContext';

const API_URL = '/api/admin/attendance/';
const STAFF_URL = '/api/admin/staff/';

const getCurrentPayrollPeriod = () => {
    const now = new Date();
    return `${now.getMonth() + 1}-${now.getFullYear()}`;
};

const buildDefaultDay = () => ({
    absent_date: '',
    reason: '',
    do_not_deduct: false,
    payroll_period: getCurrentPayrollPeriod(),
});

const buildDefaultFormData = () => ({
    staff: '',
    absent_type: 'ABSENT',
    entry_mode: 'SPECIFIC_DAYS',
    start_date: '',
    end_date: '',
    reason: '',
    do_not_deduct: false,
    payroll_period: getCurrentPayrollPeriod(),
    days: [buildDefaultDay()],
});

const Attendance = () => {
    const { hasPermission } = usePermissions();
    const [records, setRecords] = useState([]);
    const [activeTab, setActiveTab] = useState('ALL');
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingRecordId, setEditingRecordId] = useState(null);
    const [staffOptions, setStaffOptions] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [formData, setFormData] = useState(buildDefaultFormData());
    const [filterAbsentType, setFilterAbsentType] = useState('');
    const [modalDocs, setModalDocs] = useState([]);
    const [modalUploading, setModalUploading] = useState(false);
    const [viewRecord, setViewRecord] = useState(null);

    const isAbsentType = useMemo(() => formData.absent_type === 'ABSENT', [formData.absent_type]);

    const requiresReason = useMemo(
        () => ['ABSENT', 'SICK'].includes(formData.absent_type),
        [formData.absent_type]
    );

    const showReason = useMemo(
        () => ['ABSENT', 'SICK', 'LEAVE'].includes(formData.absent_type),
        [formData.absent_type]
    );

    const typeOptions = [
        { id: 'ABSENT', text: 'ABSENT' },
        { id: 'SICK', text: 'SICK' },
        { id: 'LEAVE', text: 'LEAVE' },
    ];

    const modeOptions = [
        { id: 'SPECIFIC_DAYS', text: 'Specific Days' },
        { id: 'DATE_RANGE', text: 'Date Range' },
    ];

    const resetForm = () => {
        setFormData(buildDefaultFormData());
        setEditingRecordId(null);
        setModalDocs([]);
    };

    const payrollMonthOptions = useMemo(() => {
        const options = [];
        const now = new Date();

        for (let i = 0; i < 3; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            const label = date.toLocaleString('default', { month: 'long' });
            options.push({ id: `${month}-${year}`, text: `${label} ${year}`, month, year });
        }

        return options;
    }, []);

    const canCreate = hasPermission('ATTENDANCE', 'can_create');
    const canEdit = hasPermission('ATTENDANCE', 'can_edit');
    const canDelete = hasPermission('ATTENDANCE', 'can_delete');

    const fetchStaff = async () => {
        try {
            const res = await fetch(`${STAFF_URL}?page_size=1000`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch staff');
            const data = await res.json();
            const staff = data.results || data;
            setStaffOptions([
                { id: 'ALL', text: 'ALL' },
                ...staff.map(item => ({ id: item.id, text: `${item.first_name} ${item.last_name}`.trim() || item.name }))
            ]);
        } catch (error) {
            toast.error(error.message);
        }
    };

    const fetchRecords = async () => {
        if (!selectedStaff) {
            setRecords([]);
            return;
        }

        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (selectedStaff !== 'ALL') {
                params.append('staff_id', selectedStaff);
            }
            const typeFilter = filterAbsentType || (activeTab !== 'ALL' ? activeTab : '');
            if (typeFilter) {
                params.append('absent_type', typeFilter);
            }
            if (filterStartDate) {
                params.append('start_date', filterStartDate);
            }
            if (filterEndDate) {
                params.append('end_date', filterEndDate);
            }

            const url = `${API_URL}?${params.toString()}`;
            const res = await fetch(url, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch attendance history');
            const data = await res.json();
            setRecords(data.results || data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStaff();
    }, []);

    useEffect(() => {
        if (selectedStaff) {
            fetchRecords();
        } else {
            setRecords([]);
        }
    }, [activeTab]);

    const updateDay = (index, field, value) => {
        const nextDays = [...formData.days];
        nextDays[index] = { ...nextDays[index], [field]: value };
        setFormData(prev => ({ ...prev, days: nextDays }));
    };

    const addDayRow = () => {
        setFormData(prev => ({ ...prev, days: [...prev.days, buildDefaultDay()] }));
    };

    const removeDayRow = (index) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.filter((_, currentIndex) => currentIndex !== index),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isAbsentType && formData.entry_mode === 'DATE_RANGE' && !formData.do_not_deduct && !formData.payroll_period) {
            toast.error('Please select payroll month when deduction is enabled.');
            return;
        }

        if (isAbsentType && formData.entry_mode === 'SPECIFIC_DAYS') {
            for (const day of formData.days) {
                if (!day.do_not_deduct && !day.payroll_period) {
                    toast.error('Please select payroll month for each deductible absent day.');
                    return;
                }
            }
        }

        const selectedPayroll = payrollMonthOptions.find(option => option.id === formData.payroll_period);

        const payload = {
            staff: formData.staff,
            absent_type: formData.absent_type,
            entry_mode: formData.entry_mode,
        };

        if (isAbsentType && formData.entry_mode === 'DATE_RANGE') {
            payload.do_not_deduct = formData.do_not_deduct;
            payload.payroll_month = formData.do_not_deduct ? null : selectedPayroll?.month;
            payload.payroll_year = formData.do_not_deduct ? null : selectedPayroll?.year;
        }

        if (formData.entry_mode === 'DATE_RANGE') {
            payload.start_date = formData.start_date;
            payload.end_date = formData.end_date;
            payload.reason = showReason ? formData.reason : '';
        } else {
            payload.days = formData.days.map(day => ({
                absent_date: day.absent_date,
                reason: showReason ? day.reason : '',
                do_not_deduct: isAbsentType ? !!day.do_not_deduct : true,
                payroll_month: isAbsentType && !day.do_not_deduct
                    ? payrollMonthOptions.find(option => option.id === day.payroll_period)?.month
                    : null,
                payroll_year: isAbsentType && !day.do_not_deduct
                    ? payrollMonthOptions.find(option => option.id === day.payroll_period)?.year
                    : null,
            }));
        }

        try {
            const endpoint = editingRecordId ? `${API_URL}${editingRecordId}/` : API_URL;
            const res = await fetch(endpoint, {
                method: editingRecordId ? 'PATCH' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(
                    errorData.detail ||
                    errorData.days?.[0] ||
                    errorData.reason?.[0] ||
                    errorData.start_date?.[0] ||
                    'Failed to create attendance record'
                );
            }

            toast.success(editingRecordId ? 'Attendance record updated successfully' : 'Attendance record created successfully');
            setShowModal(false);
            resetForm();
            fetchRecords();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const formatDate = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString();
    };

    const handleEdit = (record) => {
        const payrollPeriod = record.payroll_month && record.payroll_year
            ? `${record.payroll_month}-${record.payroll_year}`
            : '';

        setEditingRecordId(record.id);
        setFormData({
            staff: record.staff,
            absent_type: record.absent_type,
            entry_mode: record.entry_mode,
            start_date: record.start_date || '',
            end_date: record.end_date || '',
            reason: record.reason || '',
            do_not_deduct: !!record.do_not_deduct,
            payroll_period: payrollPeriod,
            days: record.entry_mode === 'SPECIFIC_DAYS'
                ? (record.days || []).map(day => ({
                    absent_date: day.absent_date,
                    reason: day.reason || '',
                    do_not_deduct: !!day.do_not_deduct,
                    payroll_period: day.payroll_month && day.payroll_year
                        ? `${day.payroll_month}-${day.payroll_year}`
                        : '',
                }))
                : [buildDefaultDay()],
        });
        setModalDocs(record.documents || []);
        setShowModal(true);
    };

    const [confirmDialog, setConfirmDialog] = useState({ open: false, recordId: null });
    const [docDeleteConfirm, setDocDeleteConfirm] = useState({ open: false, docId: null });

    const handleModalDocUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !editingRecordId) return;
        setModalUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${API_URL}${editingRecordId}/upload-document/`, {
                method: 'POST',
                headers: { 'Authorization': getAuthHeaders().Authorization },
                body: fd,
            });
            if (!res.ok) throw new Error('Upload failed');
            const newDoc = await res.json();
            setModalDocs(prev => [...prev, newDoc]);
            setRecords(prev => prev.map(r =>
                r.id === editingRecordId ? { ...r, documents: [...(r.documents || []), newDoc] } : r
            ));
            toast.success('Document uploaded');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setModalUploading(false);
        }
        e.target.value = '';
    };

    const confirmDeleteDoc = (docId) => setDocDeleteConfirm({ open: true, docId });

    const doDeleteDoc = async () => {
        const { docId } = docDeleteConfirm;
        setDocDeleteConfirm({ open: false, docId: null });
        try {
            const res = await fetch(`${API_URL}${editingRecordId}/documents/${docId}/`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Delete failed');
            const updatedDocs = modalDocs.filter(d => d.id !== docId);
            setModalDocs(updatedDocs);
            setRecords(prev => prev.map(r =>
                r.id === editingRecordId ? { ...r, documents: updatedDocs } : r
            ));
            toast.success('Document deleted');
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDelete = (recordId) => {
        setConfirmDialog({ open: true, recordId });
    };

    const doDelete = async () => {
        const recordId = confirmDialog.recordId;
        setConfirmDialog({ open: false, recordId: null });
        try {
            const res = await fetch(`${API_URL}${recordId}/`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete attendance record');
            toast.success('Attendance record deleted');
            fetchRecords();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const { items: sortedRecords, requestSort, getSortIndicator } = useSortableTable(records);

    const buildRecordRows = (recordList) => {
        const rows = [];
        recordList.forEach(record => {
            if (record.entry_mode === 'SPECIFIC_DAYS') {
                (record.days || []).forEach((day, index) => {
                    rows.push({
                        key: `${record.id}-${day.id || index}`,
                        record,
                        dateText: formatDate(day.absent_date),
                        reasonText: (day.reason || '-').toUpperCase(),
                        rowSpan: index === 0 ? (record.days || []).length : 0,
                        showRecordCells: index === 0,
                    });
                });
            } else {
                rows.push({
                    key: `${record.id}-range`,
                    record,
                    dateText: `${formatDate(record.start_date)}${record.end_date && record.end_date !== record.start_date ? ` to ${formatDate(record.end_date)}` : ''}`,
                    reasonText: (record.reason || '-').toUpperCase(),
                    rowSpan: 1,
                    showRecordCells: true,
                });
            }
        });
        return rows;
    };

    const tableRows = useMemo(() => {
        if (selectedStaff === 'ALL') {
            const grouped = {};
            sortedRecords.forEach(record => {
                const staff = record.staff_name || 'Unknown';
                if (!grouped[staff]) grouped[staff] = [];
                grouped[staff].push(record);
            });
            const sortedStaffNames = Object.keys(grouped).sort();
            const rows = [];
            sortedStaffNames.forEach(staffName => {
                const staffRecords = grouped[staffName].slice().sort((a, b) => {
                    const dateA = a.start_date || (a.days && a.days[0] ? a.days[0].absent_date : '') || '';
                    const dateB = b.start_date || (b.days && b.days[0] ? b.days[0].absent_date : '') || '';
                    return dateB.localeCompare(dateA);
                });
                rows.push({ isGroupHeader: true, key: `group-${staffName}`, staffName });
                buildRecordRows(staffRecords).forEach(r => rows.push(r));
            });
            return rows;
        }
        return buildRecordRows(sortedRecords);
    }, [sortedRecords, selectedStaff]);

    return (
        <div className="page-container">
            <div className="admin-header">
                <h2>Attendance</h2>
                <button className="btn btn-primary" disabled={!canCreate} onClick={() => { resetForm(); setShowModal(true); }}>
                    <i className="fa fa-plus"></i> New Absent Record
                </button>
            </div>

            <div className="filters-container" style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
                <div style={{ width: 280 }}>
                    <label className="required">Staff</label>
                    <Select2Dropdown
                        options={staffOptions}
                        value={selectedStaff}
                        onChange={setSelectedStaff}
                        displayKey="text"
                        valueKey="id"
                    />
                </div>
                <div style={{ width: 180 }}>
                    <label>Start Date (Optional)</label>
                    <input type="date" className="form-control" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} />
                </div>
                <div style={{ width: 180 }}>
                    <label>End Date (Optional)</label>
                    <input type="date" className="form-control" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} />
                </div>
                <div style={{ width: 160 }}>
                    <label>Attendance Type</label>
                    <Select2Dropdown
                        options={[
                            { id: '', text: 'ALL TYPES' },
                            { id: 'ABSENT', text: 'ABSENT' },
                            { id: 'SICK', text: 'SICK' },
                            { id: 'LEAVE', text: 'LEAVE' },
                        ]}
                        value={filterAbsentType}
                        onChange={val => setFilterAbsentType(val || '')}
                        displayKey="text"
                        valueKey="id"
                        placeholder="ALL TYPES"
                        clearable={true}
                    />
                </div>
                <div>
                    <button className="btn btn-primary" style={{ marginTop: 24 }} onClick={fetchRecords} disabled={!selectedStaff}>Load Records</button>
                </div>
            </div>

            <div className="custom-tabs-container" style={{ marginTop: 16 }}>
                <button className={`custom-tab ${activeTab === 'ALL' ? 'active' : ''}`} onClick={() => setActiveTab('ALL')}>All Records</button>
                <button className={`custom-tab ${activeTab === 'ABSENT' ? 'active' : ''}`} onClick={() => setActiveTab('ABSENT')}>Absent</button>
                <button className={`custom-tab ${activeTab === 'SICK' ? 'active' : ''}`} onClick={() => setActiveTab('SICK')}>Sick</button>
                <button className={`custom-tab ${activeTab === 'LEAVE' ? 'active' : ''}`} onClick={() => setActiveTab('LEAVE')}>Leave</button>
            </div>

            <div className="table-responsive" style={{ marginTop: 16 }}>
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('staff_name')} style={{ cursor: 'pointer' }}>Staff {getSortIndicator('staff_name')}</th>
                            <th onClick={() => requestSort('absent_type')} style={{ cursor: 'pointer' }}>Type {getSortIndicator('absent_type')}</th>
                            <th onClick={() => requestSort('start_date')} style={{ cursor: 'pointer' }}>Date {getSortIndicator('start_date')}</th>
                            <th onClick={() => requestSort('absent_days_count')} style={{ cursor: 'pointer' }}>Total Days {getSortIndicator('absent_days_count')}</th>
                            <th onClick={() => requestSort('reason')} style={{ cursor: 'pointer' }}>Reason {getSortIndicator('reason')}</th>
                            <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer' }}>Created On {getSortIndicator('created_at')}</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!selectedStaff && (
                            <tr>
                                <td colSpan={7} className="no-data">Please select Staff to view attendance records.</td>
                            </tr>
                        )}
                        {selectedStaff && !loading && tableRows.filter(r => !r.isGroupHeader).length === 0 && (
                            <tr>
                                <td colSpan={7} className="no-data">No attendance records found.</td>
                            </tr>
                        )}
                        {tableRows.map(row => {
                            if (row.isGroupHeader) {
                                return (
                                    <tr key={row.key} style={{ backgroundColor: '#dbeafe' }}>
                                        <td colSpan={7} style={{ fontWeight: 'bold', padding: '6px 12px', color: '#1e40af', fontSize: '1.0em', textTransform: 'uppercase' }}>
                                            {row.staffName}
                                        </td>
                                    </tr>
                                );
                            }
                            return (
                            <tr key={row.key}>
                                {row.showRecordCells && <td rowSpan={row.rowSpan}>{row.record.staff_name}</td>}
                                {row.showRecordCells && <td rowSpan={row.rowSpan}>{row.record.absent_type}</td>}
                                <td>{row.dateText}</td>
                                {row.showRecordCells && <td rowSpan={row.rowSpan}>{row.record.absent_days_count}</td>}
                                <td>{row.reasonText}</td>
                                {row.showRecordCells && <td rowSpan={row.rowSpan}>{formatDate(row.record.created_at)}</td>}
                                {row.showRecordCells && (
                                    <td rowSpan={row.rowSpan}>
                                        <button className="btn-icon" onClick={() => setViewRecord(row.record)} title="View">
                                            <i className="fa fa-eye"></i>
                                        </button>
                                        {canEdit && (
                                            <button className="btn-icon" onClick={() => handleEdit(row.record)} title="Edit">
                                                <i className="fa fa-pencil"></i>
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button className="btn-icon btn-icon-delete" onClick={() => handleDelete(row.record.id)} title="Delete">
                                                <i className="fa fa-trash"></i>
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '700px', padding: 0, overflow: 'hidden' }}>
                        <button className="modal-close-btn" onClick={() => { setShowModal(false); resetForm(); }} style={{ zIndex: 10 }}>&times;</button>

                        <div className="dialog-container" style={{ boxShadow: 'none', border: 'none', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div className="dialog-header">
                                <h3>{editingRecordId ? 'Edit Attendance Record' : 'Add Attendance Record'}</h3>
                            </div>

                            <div className="dialog-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
                                <form id="attendance-form" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label className="required">Staff</label>
                                        <Select2Dropdown
                                            options={staffOptions}
                                            value={formData.staff}
                                            onChange={(value) => setFormData(prev => ({ ...prev, staff: value }))}
                                            displayKey="text"
                                            valueKey="id"
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 form-group">
                                            <label className="required">Absent Type</label>
                                            <Select2Dropdown
                                                options={typeOptions}
                                                value={formData.absent_type}
                                                onChange={(value) => setFormData(prev => ({
                                                    ...prev,
                                                    absent_type: value,
                                                    reason: '',
                                                    do_not_deduct: false,
                                                    payroll_period: getCurrentPayrollPeriod(),
                                                    days: prev.days.map(day => ({ ...day, reason: '', do_not_deduct: false, payroll_period: getCurrentPayrollPeriod() })),
                                                }))}
                                                displayKey="text"
                                                valueKey="id"
                                            />
                                        </div>
                                        <div className="col-md-6 form-group">
                                            <label className="required">Date Entry</label>
                                            <Select2Dropdown
                                                options={modeOptions}
                                                value={formData.entry_mode}
                                                onChange={(value) => setFormData(prev => ({ ...prev, entry_mode: value, start_date: '', end_date: '', reason: '', do_not_deduct: false, payroll_period: getCurrentPayrollPeriod(), days: [buildDefaultDay()] }))}
                                                displayKey="text"
                                                valueKey="id"
                                            />
                                        </div>
                                    </div>

                                    {isAbsentType && formData.entry_mode === 'DATE_RANGE' && (
                                        <div className="row">
                                            <div className="col-md-6 form-group" style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                                                <label style={{ marginBottom: 0 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.do_not_deduct}
                                                        onChange={e => setFormData(prev => ({
                                                            ...prev,
                                                            do_not_deduct: e.target.checked,
                                                            payroll_period: e.target.checked ? '' : (prev.payroll_period || getCurrentPayrollPeriod()),
                                                        }))}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    Do not deduct
                                                </label>
                                            </div>
                                            <div className="col-md-6 form-group">
                                                <label className={formData.do_not_deduct ? '' : 'required'}>Apply to:</label>
                                                <Select2Dropdown
                                                    options={payrollMonthOptions}
                                                    value={formData.payroll_period}
                                                    onChange={value => setFormData(prev => ({ ...prev, payroll_period: value }))}
                                                    displayKey="text"
                                                    valueKey="id"
                                                    disabled={formData.do_not_deduct}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {formData.entry_mode === 'DATE_RANGE' ? (
                                        <>
                                            <div className="row">
                                                <div className="col-md-6 form-group">
                                                    <label className="required">Start Date</label>
                                                    <input type="date" className="form-control" value={formData.start_date} onChange={e => setFormData(prev => ({ ...prev, start_date: e.target.value }))} required />
                                                </div>
                                                <div className="col-md-6 form-group">
                                                    <label className="required">End Date</label>
                                                    <input type="date" className="form-control" value={formData.end_date} onChange={e => setFormData(prev => ({ ...prev, end_date: e.target.value }))} required />
                                                </div>
                                            </div>

                                            {showReason && (
                                                <div className="form-group">
                                                    <label className={requiresReason ? 'required' : ''}>Reason</label>
                                                    <textarea className="form-control" rows="3" value={formData.reason} onChange={e => setFormData(prev => ({ ...prev, reason: e.target.value.toUpperCase() }))} required={requiresReason} />
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="form-group">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <label className="required" style={{ marginBottom: 0 }}>Specific Days</label>
                                                <button type="button" className="btn btn-sm btn-secondary" onClick={addDayRow}>Add Day</button>
                                            </div>
                                            {formData.days.map((day, index) => (
                                                <div key={index} className="row" style={{ marginBottom: 12 }}>
                                                    <div className={isAbsentType ? (showReason ? 'col-md-3' : 'col-md-4') : (showReason ? 'col-md-4' : 'col-md-10')}>
                                                        <input type="date" className="form-control" value={day.absent_date} onChange={e => updateDay(index, 'absent_date', e.target.value)} required />
                                                    </div>
                                                    {showReason && (
                                                        <div className={isAbsentType ? 'col-md-3' : 'col-md-6'}>
                                                            <input type="text" className="form-control" placeholder="Reason" value={day.reason} onChange={e => updateDay(index, 'reason', e.target.value.toUpperCase())} required={requiresReason} />
                                                        </div>
                                                    )}

                                                    {isAbsentType && (
                                                        <>
                                                            <div className="col-md-2" style={{ display: 'flex', alignItems: 'center' }}>
                                                                <label style={{ marginBottom: 0 }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!day.do_not_deduct}
                                                                        onChange={e => setFormData(prev => {
                                                                            const nextDays = [...prev.days];
                                                                            const currentDay = nextDays[index] || buildDefaultDay();
                                                                            nextDays[index] = {
                                                                                ...currentDay,
                                                                                do_not_deduct: e.target.checked,
                                                                                payroll_period: e.target.checked ? '' : (currentDay.payroll_period || getCurrentPayrollPeriod()),
                                                                            };
                                                                            return { ...prev, days: nextDays };
                                                                        })}
                                                                        style={{ marginRight: 8 }}
                                                                    />
                                                                    Do not deduct
                                                                </label>
                                                            </div>
                                                            <div className="col-md-2">
                                                                <Select2Dropdown
                                                                    options={payrollMonthOptions}
                                                                    value={day.payroll_period || ''}
                                                                    onChange={value => updateDay(index, 'payroll_period', value)}
                                                                    displayKey="text"
                                                                    valueKey="id"
                                                                    disabled={!!day.do_not_deduct}
                                                                />
                                                            </div>
                                                        </>
                                                    )}

                                                    <div className={isAbsentType ? 'col-md-2' : 'col-md-2'}>
                                                        <button type="button" className="btn btn-danger" style={{ width: '100%' }} onClick={() => removeDayRow(index)} disabled={formData.days.length === 1}>Remove</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </form>

                                {editingRecordId && (
                                    <div style={{ marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                            <label style={{ marginBottom: 0, fontWeight: 600 }}>
                                                <i className="fa fa-paperclip" style={{ marginRight: 6 }}></i>
                                                Attachments{modalDocs.length > 0 && <span style={{ fontWeight: 'normal', color: '#6b7280', marginLeft: 4 }}>({modalDocs.length})</span>}
                                            </label>
                                            {canEdit && (
                                                <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer', marginBottom: 0 }}>
                                                    {modalUploading ? 'Uploading...' : <><i className="fa fa-upload" style={{ marginRight: 6 }}></i>Upload</>}
                                                    <input type="file" style={{ display: 'none' }} onChange={handleModalDocUpload} disabled={modalUploading} />
                                                </label>
                                            )}
                                        </div>
                                        {modalDocs.length === 0 ? (
                                            <p className="text-muted" style={{ marginBottom: 0, fontSize: '0.9em' }}>No documents attached.</p>
                                        ) : (
                                            <table className="admin-table" style={{ marginBottom: 0 }}>
                                                <thead>
                                                    <tr>
                                                        <th>File Name</th>
                                                        <th>Uploaded By</th>
                                                        <th>Date</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {modalDocs.map(doc => (
                                                        <tr key={doc.id}>
                                                            <td style={{ wordBreak: 'break-all' }}>{doc.file_name}</td>
                                                            <td>{doc.uploaded_by_name || '-'}</td>
                                                            <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '-'}</td>
                                                            <td>
                                                                <button className="btn-icon" title="View" onClick={() => openFilePreview(doc.file_url, doc.file_name)}>
                                                                    <i className="fa fa-eye"></i>
                                                                </button>
                                                                {canEdit && (
                                                                    <button className="btn-icon btn-icon-delete" title="Delete" onClick={() => confirmDeleteDoc(doc.id)}>
                                                                        <i className="fa fa-trash"></i>
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="dialog-footer">
                                <button type="submit" form="attendance-form" className="dialog-btn dialog-btn-primary">{editingRecordId ? 'Update Record' : 'Create Record'}</button>
                                <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewRecord && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '660px', padding: 0, overflow: 'hidden' }}>
                        <button className="modal-close-btn" onClick={() => setViewRecord(null)} style={{ zIndex: 10 }}>&times;</button>
                        <div className="dialog-container" style={{ boxShadow: 'none', border: 'none', display: 'flex', flexDirection: 'column' }}>
                            <div className="dialog-header">
                                <h3>View Attendance Record</h3>
                            </div>
                            <div className="dialog-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
                                <div className="row">
                                    <div className="col-md-6 form-group">
                                        <label>Staff</label>
                                        <div className="form-control-static">{viewRecord.staff_name || '-'}</div>
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label>Type</label>
                                        <div className="form-control-static">{viewRecord.absent_type || '-'}</div>
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label>Total Days</label>
                                        <div className="form-control-static">{viewRecord.absent_days_count ?? '-'}</div>
                                    </div>
                                </div>

                                {viewRecord.entry_mode === 'DATE_RANGE' ? (
                                    <>
                                        <div className="row">
                                            <div className="col-md-6 form-group">
                                                <label>Start Date</label>
                                                <div className="form-control-static">{formatDate(viewRecord.start_date)}</div>
                                            </div>
                                            <div className="col-md-6 form-group">
                                                <label>End Date</label>
                                                <div className="form-control-static">{formatDate(viewRecord.end_date)}</div>
                                            </div>
                                        </div>
                                        {viewRecord.reason && (
                                            <div className="form-group">
                                                <label>Reason</label>
                                                <div className="form-control-static">{viewRecord.reason}</div>
                                            </div>
                                        )}
                                        {viewRecord.absent_type === 'ABSENT' && (
                                            <div className="row">
                                                <div className="col-md-6 form-group">
                                                    <label>Do Not Deduct</label>
                                                    <div className="form-control-static">{viewRecord.do_not_deduct ? 'Yes' : 'No'}</div>
                                                </div>
                                                {!viewRecord.do_not_deduct && (viewRecord.payroll_month || viewRecord.payroll_year) && (
                                                    <div className="col-md-6 form-group">
                                                        <label>Payroll Period</label>
                                                        <div className="form-control-static">
                                                            {new Date(viewRecord.payroll_year, viewRecord.payroll_month - 1).toLocaleString('default', { month: 'long' })} {viewRecord.payroll_year}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="form-group">
                                        <label>Specific Days</label>
                                        <table className="admin-table" style={{ marginTop: 4 }}>
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Reason</th>
                                                    {viewRecord.absent_type === 'ABSENT' && <th>Do Not Deduct</th>}
                                                    {viewRecord.absent_type === 'ABSENT' && <th>Payroll Period</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(viewRecord.days || []).map((day, i) => (
                                                    <tr key={i}>
                                                        <td>{formatDate(day.absent_date)}</td>
                                                        <td>{day.reason || '-'}</td>
                                                        {viewRecord.absent_type === 'ABSENT' && <td>{day.do_not_deduct ? 'Yes' : 'No'}</td>}
                                                        {viewRecord.absent_type === 'ABSENT' && (
                                                            <td>
                                                                {!day.do_not_deduct && day.payroll_month && day.payroll_year
                                                                    ? `${new Date(day.payroll_year, day.payroll_month - 1).toLocaleString('default', { month: 'long' })} ${day.payroll_year}`
                                                                    : '-'}
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {(viewRecord.documents || []).length > 0 && (
                                    <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
                                        <label style={{ fontWeight: 600 }}>
                                            <i className="fa fa-paperclip" style={{ marginRight: 6 }}></i>
                                            Attachments ({viewRecord.documents.length})
                                        </label>
                                        <table className="admin-table" style={{ marginTop: 6 }}>
                                            <thead>
                                                <tr><th>File Name</th><th>Uploaded By</th><th>Date</th><th></th></tr>
                                            </thead>
                                            <tbody>
                                                {viewRecord.documents.map(doc => (
                                                    <tr key={doc.id}>
                                                        <td style={{ wordBreak: 'break-all' }}>{doc.file_name}</td>
                                                        <td>{doc.uploaded_by_name || '-'}</td>
                                                        <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '-'}</td>
                                                        <td>
                                                            <button className="btn-icon" title="View" onClick={() => openFilePreview(doc.file_url, doc.file_name)}>
                                                                <i className="fa fa-eye"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div style={{ marginTop: 14, color: '#6b7280', fontSize: '0.85em' }}>
                                    Created: {formatDate(viewRecord.created_at)}
                                </div>
                            </div>
                            <div className="dialog-footer">
                                <button className="dialog-btn dialog-btn-secondary" onClick={() => setViewRecord(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDialog.open}
                title="Delete Attendance Record"
                message="Are you sure you want to delete this attendance record? This action cannot be undone."
                onConfirm={doDelete}
                onCancel={() => setConfirmDialog({ open: false, recordId: null })}
            />

            <ConfirmDialog
                open={docDeleteConfirm.open}
                title="Delete Document"
                message="Are you sure you want to delete this document? This cannot be undone."
                onConfirm={doDeleteDoc}
                onCancel={() => setDocDeleteConfirm({ open: false, docId: null })}
            />

        </div>
    );
};

export default Attendance;